import type { IntervalName } from 'shared/definitions/musicTheory/intervals';
import type { AnswerAndData } from 'shared/definitions/answer-and-data';
import type Interval from '../uTheory.Notation/interval';
import InteractivePrototype from './interactivePrototype';
import type { InteractiveOptions } from './interactivePrototype';

import * as Notation from '../notation';
import QuestionGenerator from '../uTheory.Skills/questionGenerator';
import SkillCalculator from '../uTheory.Skills/skillCalculator';
import Note from '../uTheory.Notation/note';

interface IntervalGroupsOptions extends InteractiveOptions {
  lowestNote?: string | Note;
  highestNote?: string | Note;
}

export type IntervalGroupsQuestion = {
  question: Interval;
  answer: string;
  text: (string | number)[];
  skills: string;
};

class IntervalGroups extends InteractivePrototype {
  declare questions: IntervalGroupsQuestion[];

  declare currentQuestion: IntervalGroupsQuestion;

  declare options: IntervalGroupsOptions;

  skillCalculator!: SkillCalculator;

  maxQuestions!: number;

  answer: string[];

  answerAttempts: number;

  constructor(displayDiv: HTMLDivElement, options: IntervalGroupsOptions) {
    super(displayDiv, options);
    this.targetSpeedFromSkills = [IntervalGroups.registry.skills];
    this.answer = [''];
    this.answerAttempts = 0;
    this.settings = {
      instructions: {
        ariaLive: 'assertive',
        ariaLiveRegion: 0,
      },
      notation: false,
      letters: {
        customButtons: [
          { text: 'Major/minor', value: 'M/m', hotKey: ['M', 'm'] },
          { text: 'Perfect', value: 'P', hotKey: ['P', 'p'] },
        ],
        checkButton: false,
      },
      checkMark: true,
      player: {},
      oneTryOnly: false,
      progressTracker: true,
    };

    this.init(displayDiv, options);
  }

  init(displayDiv: HTMLDivElement, options: IntervalGroupsOptions): Promise<any> {
    if (options.lowestNote === undefined) {
      options.lowestNote = 'C2';
    }

    if (options.highestNote === undefined) {
      options.highestNote = 'C6';
    }

    this.skillCalculator = new SkillCalculator();
    const testMode = !!options.testMode;

    if (testMode === true) {
      this.questionPrompt = 'What interval group are <strong>{question}s</strong> in?';
    } else {
      this.questionPrompt =
        'What interval group are <strong>{question}s</strong> in?<br/> Question #{question} of {question}';
    }

    return super.init(displayDiv, options);
  }

  generateQuestions(): void {
    const options = this.options;
    this.questions = this.getQuestions(options);
    this.maxQuestions = this.questions.length;

    this.debug('Questions from options:', options.questions);
    this.debug('Questions generated from options:', this.questions);
  }

  getQuestions(options: IntervalGroupsOptions): IntervalGroupsQuestion[] {
    if (this.optionsTrue('drill')) {
      return this.generateDrillQuestions(options);
    }
    return this.generateManualQuestions(options);
  }

  generateManualQuestions(options: IntervalGroupsOptions): IntervalGroupsQuestion[] {
    return (options.questions || []).map(
      (question: string, index: number, allQuestions: string[]) =>
        this.processQuestion(question, index, allQuestions)
    );
  }

  generateDrillQuestions(options: IntervalGroupsOptions): IntervalGroupsQuestion[] {
    const generator = new QuestionGenerator();
    const skills = ['interval_groups'];
    const maxQuestions = options.maxQuestions || 8;
    const generatedQuestions = generator.generate(skills, maxQuestions);
    this.debug('Questions to process:');
    this.debug(generatedQuestions);
    return generatedQuestions.map(
      (question: string, index: number, allQuestions: string[]): IntervalGroupsQuestion =>
        this.processQuestion(question, index, allQuestions)
    );
  }

  processQuestion(question: string, index: number, allQuestions: string[]): IntervalGroupsQuestion {
    const interval = Notation.Interval.stringToInterval(question as IntervalName);
    // const skills = this.calculateSkill(interval as Interval);
    const skills = this.calculateSkill();

    const intervalText = interval.longGenericName();

    return {
      question: interval,
      answer: interval.quality,
      text: [intervalText, index + 1, allQuestions.length],
      skills,
    };
  }

  // eslint-disable-next-line class-methods-use-this
  calculateSkill(): string {
    return 'interval_groups';
  }

  // eslint-disable-next-line class-methods-use-this
  initializeMusic(): void {}

  newQuestion(): void {
    this.progressTracker!.start(this.targetSpeed);

    const interval = this.questions[this.questionNumber]!.question;
    const answer = this.questions[this.questionNumber]!.answer;

    this.debug('Question: ', interval.quality);
    this.debug('Answer: ', answer);

    this.wrongAnswerCount = 0;

    super.newQuestion();
  }

  newCheckpointQuestion(questionString: string): Promise<IntervalGroupsQuestion> {
    return this.loadingPromise.then((): Promise<IntervalGroupsQuestion> => {
      this.questions = [this.processQuestion(questionString, 0, [questionString])];
      this.newQuestion();
      this.resize();
      return Promise.resolve(this.currentQuestion);
    });
  }

  addListeners(): void {
    this.letterPanel!.addCustomButtonCallback(this.checkAnswer, this);
  }

  checkAnswer(userAnswer: string | number): void {
    if (typeof userAnswer === 'number') {
      userAnswer = `${userAnswer}`;
    }
    if (this.rightAnswerDelay) return;
    this.answer = userAnswer.split('/');
    this.answerAttempts++;

    const testMode = this.optionsTrue('testMode');
    const rightAnswer = this.questions[this.questionNumber]!.answer;
    const correct = rightAnswer === this.answer[0] || rightAnswer === this.answer[1];

    this.debug('Right answer:', rightAnswer);
    this.debug('User answer:', userAnswer);

    if (testMode === true) {
      this.checkpointAnswer(correct);
      return;
    }

    if (correct === true) {
      this.getAnswerAndData(true);
      this.rightAnswer(1000);
    } else {
      this.getAnswerAndData(false);
      this.wrongAnswer();
    }
  }

  getAnswerAndData(correct?: boolean): AnswerAndData {
    const correctAnswer = this.questions[this.questionNumber]!.answer;
    const answerAndData = {
      question: this.questions[this.questionNumber]!.question.toString(),
      questionType: 'IntervalGroups',
      questionText: (this.questionPrompt || '') as string,
      questionSVG: undefined,
      studentAnswerText: `${this.answer}`,
      correctAnswerText: `${correctAnswer}`,
      rightAnswers: correct ? 1 : 0,
      wrongAnswers: correct ? 0 : 1,
      skills: [this.questions[this.questionNumber]!.skills],
    };

    this.debug('Answer And Data:', answerAndData);
    return answerAndData;
  }

  gameOver(): void {
    this.instructionsDiv!.innerHTML = 'Nice work!';
    this.instructionsDiv!.setAttribute('role', 'alert');
    this.displayDiv.removeChild(this.lettersDiv!);

    super.gameOver();

    const progressTracker = this.progressTracker;
    this.debug('Progress Tracker: ', progressTracker);
  }

  playAnswer(): void {
    const interval = this.currentQuestion.question;
    const bFlat = new Note('Bb3');
    const intervalNote = bFlat.getNoteAt(interval);
    this.player!.play(bFlat, { startTime: 0, stopTime: 1.25 });
    this.player!.play(intervalNote as Note, { startTime: 0.5, stopTime: 1.25 });
  }

  static registry: Record<string, any> = {
    description: 'Interval Groups',
    skills: 'interval_groups',
  };
}

export default IntervalGroups;
